import Extension from "../base/extension";

class Facets extends Extension {
  forms; // Every facets form on the page (one per rendered variant: offcanvas / sidebar / inline)
  filterData = []; // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial;
  searchParamsPrev;

  /**
   * ID of the section, so we know what section to request via fetch
   * @type {String}
   */
  sectionId;

  constructor(name) {
    super(name);
  }

  /**
   * Initialisation
   */
  init() {
    // Facet forms carry a variant suffix on their id (facets-form-offcanvas /
    // -sidebar / -inline; descendant themes may still ship a bare facets-form),
    // and a page can render more than one variant (e.g. offcanvas for mobile
    // plus sidebar for desktop), so bind every match rather than the first.
    this.forms = Array.from(document.querySelectorAll("form[id^='facets-form']"));
    if(!this.forms.length) return;

    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.sectionId = document.querySelector("#products-container").dataset.sectionId;

    this.setupEventListeners();

    // Browser back/forward. Without this, filtering pushes history entries that
    // nothing listens for, so Back changes the URL and leaves the results on
    // screen untouched until a manual reload. Bound once, on window, because
    // popstate only ever fires there.
    window.addEventListener("popstate", this.onHistoryChange.bind(this));
  }

  /**
   * Re-render the results for whatever state the browser has navigated to.
   *
   * `updateURLHash: false` is the point of it: the browser has ALREADY moved the
   * URL, and pushing again here would add a fresh entry per Back press and trap
   * the shopper in the filtered view.
   *
   * The state object is what `updateURLHash` stored, but it is null for the
   * entry the page was first loaded with (that entry predates any pushState), so
   * fall back to reading the location. Without the fallback, backing out of the
   * last filter to the original URL would do nothing.
   *
   * @param {PopStateEvent} event
   */
  onHistoryChange(event) {
    const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
    if (searchParams === this.searchParamsPrev) return;
    this.renderPage(searchParams, null, false);
  }

  /**
   * Setup event listeners on every facets form
   */
  setupEventListeners() {
    this.forms.forEach((form) => {
      // Listen for form input changes - i.e. the facet inputs. We call the
      // listener after a 'debounce' period to allow the user some time to
      // complete their input (rather than immediately on input). Batch mode
      // (data-batch-apply) is per form: that form only submits via Apply.
      if (!form.hasAttribute('data-batch-apply')) {
        // debounce() already returns an arrow function closed over `this`, so
        // no extra .bind() is required (a .bind() would also create a fresh
        // reference that removeEventListener could never match).
        form.addEventListener('input', debounce((event) => {
          this.onFacetsFormInputChange(event, form);
        }, 500));
      }

      // Listen for 'facet remove' and 'facet apply' clicks. We delegate on the
      // container because buttons are re-rendered each time a filter is applied.
      form.addEventListener('click', function(event) {
        if(event.target.closest("a")?.classList.contains("facet-remove")){
          event.preventDefault();
          let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
          this.onActiveFilterClick(mockedEvent);
        }
        if(event.target.closest(".facet-apply")){
          event.preventDefault();
          this.onFacetsFormInputChange(event, form);
        }
      }.bind(this));
    });
  }

  /**
   * Handle an input change in a facets form, and render the new results
   * @param {Event} e input event
   * @param {HTMLFormElement} form the facets form the input belongs to
   */
  onFacetsFormInputChange(e, form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, e);
  }

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    document.getElementById('results-loading-faceted-results').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = element => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {String} filterDataUrl The URL we previously cached
   * @param {Event} event 
   */
  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    document.getElementById('results-loading-faceted-results').classList.add('d-none');
    this.runPostRenderHooks();
  }

  /**
   * Post-render housekeeping shared by the cache and fetch paths. The bare
   * globals are optional (descendant themes may not ship them), so guard each
   * one rather than letting a missing global break the chain before the
   * collection:filtered event dispatches.
   */
  runPostRenderHooks() {
    if (typeof flashtagLoader === "function") flashtagLoader();
    if (typeof Waypoint !== "undefined") Waypoint.refreshAll();
    const filteredEvent = new CustomEvent("collection:filtered");
    document.dispatchEvent(filteredEvent);
  }

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Facets section request failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        document.getElementById('results-loading-faceted-results').classList.add('d-none');
        this.runPostRenderHooks();
      })
      .catch((error) => {
        console.warn(this.logPrefix, "Failed to fetch filtered results; leaving current results in place.", error);
        document.getElementById('results-loading-faceted-results')?.classList.add('d-none');
      });
  }

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetLiveRegions = parsedHTML.querySelectorAll("[data-live-region^='facet-']");

    // The focused control (e.g. the checkbox just ticked) is inside a region
    // we are about to replace; remember its id so focus can be restored.
    const focusedElementId = document.activeElement?.id;

    Array.from(facetLiveRegions).forEach((element) => {
      document.querySelector(`[data-live-region="${element.dataset.liveRegion}"]`).innerHTML = element.innerHTML;
    });

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);

    // Restore focus to the re-rendered equivalent of the focused control, so
    // keyboard users are not dropped back to the top of the page. Base facet
    // ids are variant-suffixed (unique), but descendant themes may still ship
    // unsuffixed markup with duplicate ids across hidden variants, and
    // focusing a hidden element silently no-ops, so pick the visible
    // duplicate rather than whichever getElementById finds first.
    if (focusedElementId && document.activeElement === document.body) {
      const candidates = document.querySelectorAll(`[id="${CSS.escape(focusedElementId)}"]`);
      for (const candidate of candidates) {
        const visible = candidate.checkVisibility ? candidate.checkVisibility() : candidate.offsetParent !== null;
        if (visible) {
          candidate.focus({ preventScroll: true });
          break;
        }
      }
    }

    // Announce the updated results count to screen readers (clear then set,
    // so repeat counts still fire the live region).
    const resultsStatus = parsedHTML.getElementById('facets-results-status');
    const liveRegion = document.getElementById('facets-results-status');
    if (resultsStatus && liveRegion) {
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = resultsStatus.textContent.trim();
      });
    }
  }

  /**
   * Extract and replace the active facet (filter badges) HTML. Active-facet
   * containers carry a variant suffix (facets-active-offcanvas / -sidebar /
   * -inline), so pair each live container with the fetched copy by exact id.
   *
   * @param {String} html
   */
  renderActiveFacets(html) {
    document.querySelectorAll("[id^='facets-active']").forEach((liveElement) => {
      const activeFacetsElement = html.getElementById(liveElement.id);
      if (!activeFacetsElement) return;
      liveElement.outerHTML = activeFacetsElement.outerHTML;
    })
    //FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer(html) {
    document.getElementById('results-grid').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('results-grid').innerHTML;
  }

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  }

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    //this.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

export default Facets;